/*******************************************************
* progFAClaimMath
*
* Shared helpers for Prog_FA claim normalization and math.
* Canonical equation:
* COALESCE(Quantity, TakeOffQuantity) * COALESCE(Percent, 1)
********************************************************/

import { coalesceFAPercent, computeFAClaimedQuantity } from './progFAClaimQuantity';

export {
    coalesceFAPercent,
    coalesceFAQuantity,
    computeFAClaimedQuantity,
} from './progFAClaimQuantity';

export type InputMethod = 'Q' | 'P' | 'QP';

/** A RoC step's or activity type's InputMethod, or the fallback when it isn't one */
export function normalizeClaimInputMethod(value: string | null | undefined, fallback: InputMethod): InputMethod {
    return value === 'Q' || value === 'P' || value === 'QP' ? value : fallback;
}

/** A DB or user value as a finite number, or null when it is missing or not a number */
export function parseFiniteNumber(value: unknown): number | null {
    if (value == null) return null;
    const num = typeof value === 'string' ? Number(value.trim()) : Number(value);
    return Number.isFinite(num) ? num : null;
}

/** Rounds half up to the given number of decimals */
export function roundTo(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    return Math.round((value + Number.EPSILON) * factor) / factor;
}

function normalizeValueToString(value: unknown): string | null {
    if (value == null) return null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
    }
    const num = parseFiniteNumber(value);
    return num == null ? null : num.toString();
}

export function computeFAEarnedFraction(
    quantity: number | string | null | undefined,
    percent: number | string | null | undefined,
    takeOffQuantity: number | string | null | undefined
): number {
    const takeOff = parseFiniteNumber(takeOffQuantity);
    if (takeOff == null || takeOff <= 0) return 0;
    return computeFAClaimedQuantity(quantity, percent, takeOff) / takeOff;
}

export function computeFAClaimedHours(
    quantity: number | string | null | undefined,
    percent: number | string | null | undefined,
    takeOffQuantity: number | string | null | undefined,
    estimatedHours: number | string | null | undefined,
    rocMultiplier: number | string | null | undefined
): number {
    const estimated = parseFiniteNumber(estimatedHours);
    if (estimated == null || estimated <= 0) return 0;
    const roc = parseFiniteNumber(rocMultiplier) ?? 1;
    return computeFAEarnedFraction(quantity, percent, takeOffQuantity) * estimated * roc;
}

export function computeFAClaimedBudgetHours(
    quantity: number | string | null | undefined,
    percent: number | string | null | undefined,
    takeOffQuantity: number | string | null | undefined,
    budgetHours: number | string | null | undefined
): number {
    const budget = parseFiniteNumber(budgetHours);
    if (budget == null || budget <= 0) return 0;
    return computeFAEarnedFraction(quantity, percent, takeOffQuantity) * budget;
}

export function displayPercentToDbPercent(percent100: number | string | null | undefined): string | null {
    const parsed = parseFiniteNumber(percent100);
    if (parsed == null) return null;
    return (parsed / 100).toString();
}

export function dbPercentToDisplayPercent(
    percent01: number | string | null | undefined,
    defaultDisplay = ''
): string {
    const parsed = parseFiniteNumber(percent01);
    if (parsed == null) return defaultDisplay;
    const asHundred = parsed > 1.01 ? parsed : parsed * 100;
    return Math.round(asHundred).toString();
}

export function computeFAEarnedQuantity(
    earnedValue: number | string | null | undefined,
    takeOffQuantity: number | string | null | undefined
): number {
    const ev = parseFiniteNumber(earnedValue);
    const toq = parseFiniteNumber(takeOffQuantity);
    if (ev == null || toq == null) return 0;
    return ev * toq;
}

export function computeFAEarnedHours(
    earnedValue: number | string | null | undefined,
    estimatedHours: number | string | null | undefined,
    rocMultiplier: number | string | null | undefined
): number {
    const ev = parseFiniteNumber(earnedValue);
    if (ev == null) return 0;
    const estimated = parseFiniteNumber(estimatedHours);
    if (estimated == null || estimated <= 0) return 0;
    const roc = parseFiniteNumber(rocMultiplier) ?? 1;
    return ev * estimated * roc;
}

export function computeFAEarnedBudgetHours(
    earnedValue: number | string | null | undefined,
    budgetHours: number | string | null | undefined
): number {
    const ev = parseFiniteNumber(earnedValue);
    if (ev == null) return 0;
    const budget = parseFiniteNumber(budgetHours);
    if (budget == null || budget <= 0) return 0;
    return ev * budget;
}

export function normalizeFAFieldsByInputMethod(params: {
    inputMethod: string | null | undefined;
    quantityDisplay: number | string | null | undefined;
    percentDisplay: number | string | null | undefined;
}): {
    Quantity: string | null;
    Percent: string | null;
} {
    const method = params.inputMethod;
    return {
        Quantity: (method === 'Q' || method === 'QP') ? normalizeValueToString(params.quantityDisplay) : null,
        Percent: (method === 'P' || method === 'QP') ? displayPercentToDbPercent(params.percentDisplay) : null,
    };
}

/** Computes the hours allocated to one Rule of Credit on a Tracker Item. */
export function computeRocBudgetHours(
    estimatedHours: number | string | null | undefined,
    rocPercent: number | string | null | undefined
): number {
    const estimated = parseFiniteNumber(estimatedHours);
    if (estimated == null || estimated <= 0) return 0;
    const roc = parseFiniteNumber(rocPercent) ?? 1;
    return estimated * roc;
}

export function normalizeFADbFieldsByInputMethod(params: {
    inputMethod: string | null | undefined;
    quantity: number | string | null | undefined;
    percent: number | string | null | undefined;
}): {
    Quantity: string | null;
    Percent: string | null;
} {
    const method = params.inputMethod;
    return {
        Quantity: (method === 'Q' || method === 'QP') ? normalizeValueToString(params.quantity) : null,
        Percent: (method === 'P' || method === 'QP') ? normalizeValueToString(params.percent) : null,
    };
}

type ClaimProgressHistoryRecord = {
    RecordNumber?: number | null;
    TrackerID?: number | null;
    ROCID?: number | null;
    QueID?: number | null;
    Quantity: number | string | null | undefined;
    Percent: number | string | null | undefined;
    EarnedValue?: number | string | null;
};

type ClaimProgressClaim = {
    RecordNumber?: number | null;
    Quantity: number | string | null | undefined;
    Percent: number | string | null | undefined;
};

/**
 * Inputs for the shared claim progress bar on a basic Tracker Item: earned, pending from
 * other submissions, and this submission's claims, all in the bar's units (take-off
 * quantity, or 0-100 for percent-based items). This submission's claims are added
 * after pending, so they're left out of pending even once the submission is in review.
 */
export function computeTrackerClaimProgress(params: {
    takeOffQuantity: number | string | null | undefined;
    isPercentBased: boolean;
    history: readonly ClaimProgressHistoryRecord[];
    claims: readonly ClaimProgressClaim[];
    trackerID?: number | null;
    rocID?: number | null;
}) {
    const takeOff = Math.max(0, parseFiniteNumber(params.takeOffQuantity) ?? 0);
    const scale = params.isPercentBased ? 100 : takeOff;
    // Share of the take-off a record or claim covers; percent-based items fall back to Percent when there's no take-off
    const fractionOf = (record: ClaimProgressClaim) => (
        takeOff > 0
            ? computeFAClaimedQuantity(record.Quantity, record.Percent, takeOff) / takeOff
            : params.isPercentBased ? coalesceFAPercent(record.Percent) : 0
    );
    const claimRecordNumbers = new Set(params.claims
        .map(claim => claim.RecordNumber)
        .filter((recordNumber): recordNumber is number => recordNumber != null));

    let earnedFraction = 0;
    let pendingFraction = 0;
    for (const record of params.history) {
        if (params.trackerID != null && record.TrackerID !== params.trackerID) continue;
        if (params.rocID != null && record.ROCID !== params.rocID) continue;
        if (record.QueID === 3) {
            earnedFraction += parseFiniteNumber(record.EarnedValue) ?? 0;
        } else if ((record.QueID === 1 || record.QueID === 2)
            && (record.RecordNumber == null || !claimRecordNumbers.has(record.RecordNumber))) {
            pendingFraction += fractionOf(record);
        }
    }
    const claimFraction = params.claims.reduce((sum, claim) => sum + fractionOf(claim), 0);

    // Six decimals so float noise from the fractions can't read as an overclaim
    const toUnits = (fraction: number) => roundTo(fraction * scale, 6);
    // EarnedValue is a fraction stored to 6 places (e.g. 2 of 3 is 0.666667), so earned is rounded to
    // the same 2 places as the Remaining number; otherwise claiming exactly what's left reads as over
    const earnedQuantity = roundTo(earnedFraction * scale, 2);
    const pendingQuantity = toUnits(pendingFraction);
    return {
        takeOffQuantity: scale,
        earnedQuantity,
        pendingQuantity,
        claimQuantity: toUnits(claimFraction),
        claimAvailableQuantity: Math.max(0, roundTo(scale - earnedQuantity - pendingQuantity, 6)),
        claimOverlapsPending: false,
    };
}
